import React from "react"
import PropTypes from "prop-types"
import { CardTitle, Col, Row } from "reactstrap"
import { Link, useHistory } from "react-router-dom"
import { IconSVG } from "components/Common/IconSvg"
import moment from "moment"
import AppointmentAttachments from "./AppointmentAttachments"
import {
  getAppointmentServiceName,
  getConsultationTypeLabel,
} from "helpers/utils"

const AppointmentDetailsCol = ({ testDetails }) => {
  const address = testDetails?.addressSnapshot
  const addressFields = address && typeof address === "object" ? [
    address.addressLine || address.addressLine1 || address.line1,
    address.area,
    address.landmark,
    address.city,
    address.state,
    address.pinCode || address.pincode || address.postalCode,
  ].filter(Boolean) : []
  const attachments = Array.isArray(testDetails?.attachments) ? testDetails.attachments : []
  const medicines = Array.isArray(testDetails?.medicines) ? testDetails.medicines : []
  const vaccinations = Array.isArray(testDetails?.vaccinations) ? testDetails.vaccinations : []
  const imagingTests = Array.isArray(testDetails?.imagingTests) ? testDetails.imagingTests : []
  const labTests = Array.isArray(testDetails?.labTests) ? testDetails.labTests : []
  const diagnoses = Array.isArray(testDetails?.primaryDiagnosis) ? testDetails.primaryDiagnosis : []
  const hasPrescription = String(testDetails?.status || "").toLowerCase() === "completed" ||
    medicines.length > 0 || vaccinations.length > 0 ||
    imagingTests.length > 0 || labTests.length > 0

  return (
    <div className="list-unstyled parent-details__container">
      <div className="d-flex justify-content-between">
        <div className="d-flex align-items-start flex-wrap gap-2 mb-4">
          <CardTitle className="mb-0">Contact Info</CardTitle>
          {testDetails?.consultationCategory === "FREE" && (
            <span className="free-consultancy-badge">
              <span className="free-consultancy-badge__dot" aria-hidden="true" />
              Free Consultancy
            </span>
          )}
        </div>
        <div className="history-doc-cta">
          <Link to={`/edit-appointment/${testDetails._id}`}>
            Edit <IconSVG icon="edit" />
          </Link>
        </div>
      </div>
      <Row>
        <Col sm="6">
          <li>
            <h6 className="parent-details__title">Patient Name</h6>
            <p className="parent-details__value">
              {testDetails?.petObjectId?.fullName}
            </p>
          </li>
          <li>
            <h6 className="parent-details__title">Pet Type</h6>
            <p className="parent-details__value">{testDetails?.petType}</p>
          </li>
          <li>
            <h6 className="parent-details__title">Pet Breed</h6>
            <p className="parent-details__value">
              {testDetails?.petObjectId?.petBreed}
            </p>
          </li>
        </Col>
        <Col sm="6">
          <li>
            <h6 className="parent-details__title">Date</h6>
            <p className="parent-details__value">
              {moment(testDetails?.date).format("MMMM Do YYYY, h:mm:ss a")}
            </p>
          </li>
          <li className="mt-3">
            <h6 className="parent-details__title">Appointment Time</h6>
            <p className="parent-details__value">{testDetails?.time}</p>
          </li>
          <li className="mt-3">
            <h6 className="parent-details__title">Consultation Type</h6>
            <p className="parent-details__value">
              {getConsultationTypeLabel(testDetails)}
            </p>
          </li>
          <li className="mt-3">
            <h6 className="parent-details__title">Service</h6>
            <p className="parent-details__value">
              {getAppointmentServiceName(testDetails)}
            </p>
          </li>
        </Col>
        <Col sm="6">
          <li>
            <h6 className="parent-details__title">Fee</h6>
            <p className="parent-details__value">₹{testDetails?.cost}</p>
          </li>
          <li className="mt-3">
            <h6 className="parent-details__title">Appointment Status</h6>
            <p className="parent-details__value">
              {testDetails?.appointmentStatus}
            </p>
          </li>
          {!hasPrescription && testDetails?.notes && (
            <li className="mt-3">
              <h6 className="parent-details__title">Notes</h6>
              <p className="parent-details__value">{testDetails.notes}</p>
            </li>
          )}
        </Col>
        <Col sm="6">
          <li>
            <h6 className="parent-details__title">Pet Parent</h6>
            <p className="parent-details__value">
              {testDetails?.parentId?.fullName}
            </p>
          </li>
          <li className="mt-3">
            <h6 className="parent-details__title">Status</h6>
            <p className="parent-details__value">{testDetails?.status}</p>
          </li>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col sm="12"><CardTitle>Customer Address</CardTitle></Col>
        <Col sm="12"><p className="parent-details__value">
          {addressFields.length ? addressFields.join(", ") : typeof address === "string" && address.trim() ? address : "Not provided"}
        </p></Col>
        <Col sm="12"><CardTitle>Pet Problem / Concern</CardTitle></Col>
        <Col sm="12"><p className="parent-details__value" style={{ whiteSpace: "pre-wrap" }}>
          {testDetails?.problem || "Problem not provided"}
        </p></Col>
        <Col sm="12"><CardTitle>Reports / Images / Attachments</CardTitle></Col>
        <Col sm="12">
          <AppointmentAttachments appointmentId={testDetails?._id} attachments={attachments} />
        </Col>
        <Col sm="12" className="mt-3"><CardTitle>Prescription</CardTitle></Col>
        <Col sm="12">
          {hasPrescription ? (
            <div className="appointment-prescription-summary">
              {diagnoses.length > 0 && (
                <div><h6>Diagnosis</h6><p className="mb-0">{diagnoses.map(item => item?.title).filter(Boolean).join(", ")}</p></div>
              )}
              {medicines.length > 0 && (
                <div>
                  <h6>Medicines</h6>
                  {medicines.map((item, index) => (
                    <p key={index} className="mb-1">
                      <strong>{item?.medicine || item?.name || `Medicine ${index + 1}`}</strong>
                      {[item?.dosage, item?.duration, item?.instructions].filter(Boolean).length > 0 &&
                        ` — ${[item?.dosage, item?.duration, item?.instructions].filter(Boolean).join(" · ")}`}
                    </p>
                  ))}
                </div>
              )}
              {vaccinations.length > 0 && (
                <div>
                  <h6>Vaccinations</h6>
                  {vaccinations.map((item, index) => (
                    <p key={index} className="mb-1">{typeof item?.vaccine === "string" ? item.vaccine : item?.vaccine?.name || item?.vaccine?.value || `Vaccine ${index + 1}`}</p>
                  ))}
                </div>
              )}
              {imagingTests.length > 0 && (
                <div><h6>Imaging tests</h6><p className="mb-0">{imagingTests.map(item => item?.type || item?.title).filter(Boolean).join(", ")}</p></div>
              )}
              {labTests.length > 0 && (
                <div><h6>Lab tests</h6><p className="mb-0">{labTests.map(item => item?.title || item?.type).filter(Boolean).join(", ")}</p></div>
              )}
              {testDetails?.notes && <div><h6>Clinical notes</h6><p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{testDetails.notes}</p></div>}
              <Link className="btn btn-outline-primary btn-sm align-self-start" to={`/prescription-details/${testDetails._id}`}>
                Open generated prescription
              </Link>
            </div>
          ) : <p className="parent-details__value">No prescription generated for this appointment.</p>}
        </Col>
      </Row>
    </div>
  )
}

AppointmentDetailsCol.propTypes = {
  details: PropTypes.any,
}

export default AppointmentDetailsCol
